import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useTranslation } from "react-i18next";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";
import { openLeaveGamePopup } from "../../../../State/actions";
import { selectLeaveGamePopupActive } from "../../../../State/selectors";

// Position config — extracted for easy tweaking
const HOME_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.75,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.8,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.8,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.8,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => buttonConfig.width,
    y: (toolbar) => toolbar.height / 2,
  },
};

const ButtonHome = ({ toolbar }) => {
  const { t } = useTranslation();
  const { getResponsiveConfig, device, orientation } = useDimensions();
  const dispatch = useDispatch();
  const leaveGamePopupActive = useSelector(selectLeaveGamePopupActive);

  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();

  const texture = getUITexture("icon_home");
  const activeTexture = getUITexture("icon_home_active");

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Position using config
  const buttonPosition = useMemo(() => {
    const deviceConfig = HOME_BUTTON_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig),
      y: posConfig.y(toolbar, buttonConfig),
    };
  }, [device, orientation, toolbar, buttonConfig]);

  const handleClick = () => {
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();
    dispatch(openLeaveGamePopup());
  };

  return (
    <Container
      name="secondary_button_home"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={leaveGamePopupActive ? activeTexture : texture}
          name="secondary_button_home"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessibleTitle={t("home")}
          accessibleHint={t("home")}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonHome;
